import { getStartOf, addTime, isAfterDate, isInRange, stringifyDateToISO8601 } from './dates';

// RAS-2509: caps day-by-day iteration as a guard against malformed API data (e.g. a
// slot/range with start after end) producing a runaway loop.
const MAX_DAYS_TO_ENUMERATE = 400;

/**
 * Every calendar date ('YYYY-MM-DD' in timeZone) in [rangeStart, rangeEnd) - end is
 * exclusive, matching Sharetribe's own time-slot/booking convention.
 */
export const enumerateDateStrings = (rangeStart, rangeEnd, timeZone) => {
  const dates = [];
  if (!rangeStart || !rangeEnd) return dates;

  let cursor = getStartOf(rangeStart, 'day', timeZone);
  const endBoundary = getStartOf(rangeEnd, 'day', timeZone);
  let dayCount = 0;
  while (isAfterDate(endBoundary, cursor) && dayCount < MAX_DAYS_TO_ENUMERATE) {
    dates.push(stringifyDateToISO8601(cursor, timeZone));
    cursor = addTime(cursor, 1, 'days', timeZone);
    dayCount += 1;
  }
  return dates;
};

/**
 * RAS-2509 follow-up: blocked dates for ONE listing over [rangeStart, rangeEnd) -
 * every date in the range for which no time slot covers that day's exact midnight
 * instant. `timeSlots` is the flat, denormalised array (propTypes.timeSlot: each with
 * `.attributes.start`/`.attributes.end`, end exclusive).
 *
 * This MUST stay in sync with BookingDatesForm.js's own `timeSlotsContain`/
 * `isDayBlockedFn` (the listing page's own, already-correct calendar) - both need to
 * answer the exact same question ("is this day available for this listing") from the
 * same Sharetribe time-slot data, and a divergence between them is exactly what
 * caused this bug. An earlier version of this function instead expanded each slot
 * into calendar dates by rounding the slot's start/end down to day boundaries, which
 * is wrong whenever a slot boundary falls mid-day (routine - a slot boundary is
 * wherever an adjacent booking starts/ends, e.g. a 10am checkout): rounding a slot
 * that only starts at 10am down to that day's midnight made the WHOLE day register as
 * available, even though the morning was still occupied. Matching
 * `timeSlotsContain`'s check exactly - does this day's own midnight instant fall
 * inside `[slot.start, slot.end)` - avoids that rounding entirely.
 */
export const computeListingBlockedDates = (timeSlots, rangeStart, rangeEnd, timeZone) => {
  const slots = (timeSlots || []).filter(s => s?.attributes?.start && s?.attributes?.end);
  const blocked = [];
  if (!rangeStart || !rangeEnd) return blocked;

  let cursor = getStartOf(rangeStart, 'day', timeZone);
  const endBoundary = getStartOf(rangeEnd, 'day', timeZone);
  let dayCount = 0;
  while (isAfterDate(endBoundary, cursor) && dayCount < MAX_DAYS_TO_ENUMERATE) {
    const isAvailable = slots.some(slot =>
      isInRange(cursor, slot.attributes.start, slot.attributes.end, 'hour', timeZone)
    );
    if (!isAvailable) {
      blocked.push(stringifyDateToISO8601(cursor, timeZone));
    }
    cursor = addTime(cursor, 1, 'days', timeZone);
    dayCount += 1;
  }
  return blocked;
};

/**
 * RAS-2509: group-level blocked dates are the UNION across every listing in the
 * group - a date blocked for ANY single listing is blocked for the whole group,
 * since a date-edit moves every item in the group to the same new dates together.
 */
export const unionBlockedDates = (perListingBlockedDateArrays = []) => {
  const union = new Set();
  perListingBlockedDateArrays.forEach(dates => {
    (dates || []).forEach(d => union.add(d));
  });
  return union;
};

/**
 * RAS-2506 follow-up: same union rule as unionBlockedDates, but sourced from a
 * per-listing map (`{ [listingId]: Set<string> }`) and scoped to only the
 * `listingIds` currently passed in. This is what lets a listing's blocked dates
 * disappear from the group view the instant it leaves the group (e.g. removed from
 * the cart) - the map can go on holding that listing's stale data (harmless, and
 * lets it come straight back if the listing rejoins the group later), because
 * nothing here ever reads it once its id isn't in `listingIds` anymore.
 */
export const deriveGroupBlockedDates = (listingIds = [], blockedDatesByListing = {}) => {
  const union = new Set();
  listingIds.forEach(id => {
    (blockedDatesByListing[id] || new Set()).forEach(d => union.add(d));
  });
  return union;
};

/**
 * RAS-2506 follow-up: the group-level counterpart to `checkedDates` (see
 * useCartGroupAvailability.js's own doc comment for what "checked" means), sourced
 * from a per-listing map and scoped to the current `listingIds` - but as an
 * INTERSECTION, not a union. A date only counts as "known" for the group once EVERY
 * current listing has verified it; if it were a union, a listing newly added to the
 * group would incorrectly inherit the rest of the group's already-checked dates
 * before its own availability had ever been fetched, defeating the whole point of
 * the checked/blocked-by-default distinction for that listing.
 */
export const deriveGroupCheckedDates = (listingIds = [], checkedDatesByListing = {}) => {
  if (!listingIds.length) return new Set();

  const [firstId, ...restIds] = listingIds;
  const intersection = new Set(checkedDatesByListing[firstId] || []);
  restIds.forEach(id => {
    const checkedForListing = checkedDatesByListing[id] || new Set();
    [...intersection].forEach(d => {
      if (!checkedForListing.has(d)) intersection.delete(d);
    });
  });
  return intersection;
};
